import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { toServerFormat } from 'core-components/helpers/date'
import { LoaderOverlay } from 'core-components/library/loader'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { getAllWrappersInPage } from 'core-components/sitebuilder/layout/flexGridWrapper/helpers/getAllWrappersInPage'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { selectCalledFlexWrapperAction } from 'core-components/sitebuilder/store/selectors/selectCalledFlexWrapperAction'
import { selectFlexSettings } from 'core-components/sitebuilder/store/selectors/selectFlexSettings'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'
import { selectSitePages } from 'core-components/sitebuilder/store/selectors/selectSitePages'
import {
  setCalledFlexWrapperAction,
  setEditLayoutMode,
  setFlexSettings,
  setSitePages,
} from 'core-components/sitebuilder/store/siteBuilderSlice'

import {
  FlexGridWrapperActionsContext,
  IFlexGridWrapperActionsContext,
} from './context/FlexGridWrapperActionsContext'

interface IFlexGridActionWrapper {
  children: ReactNode
  settings: IFlexLayout
  sitePage: ISitePage
  setSitePage: (sitePage: ISitePage) => void
  setIsDefault: (value: boolean) => void
  setSettings: (settings: IFlexLayout) => void
  area?: string
}

export const FlexGridActionWrapper = ({
  children,
  settings,
  setIsDefault,
  sitePage,
  setSitePage,
  setSettings,
  area,
}: IFlexGridActionWrapper) => {
  const { onSaveWidgetsAsDefault, getDefaultPageSettings } = useContext(FlexGridWrapperContext)
  const routeParams = useSelector(selectRouteParams)
  const sitePages = useSelector(selectSitePages)
  const flexSettings = useSelector(selectFlexSettings)
  const calledFlexWrapperAction = useSelector(selectCalledFlexWrapperAction)
  const dispatch = useDispatch()

  const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false)

  const exportSettings = useCallback(async () => {
    const newSettings = JSON.parse(JSON.stringify(settings))
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(newSettings)
    )}`
    const dlAnchorElem = document.createElement('a')
    dlAnchorElem.setAttribute('href', dataStr)
    dlAnchorElem.setAttribute('download', `${sitePage.id}.json`)
    dlAnchorElem.click()
  }, [settings, sitePage.id])

  const deleteLocalSettings = useCallback(async () => {
    const newFlexSettings = structuredClone(flexSettings)
    const allWrappersInPage = getAllWrappersInPage(sitePage.id, flexSettings)
    if (Object.keys(allWrappersInPage).length > 0) {
      for (const i in allWrappersInPage) {
        delete newFlexSettings[i]
      }
      dispatch(setFlexSettings(newFlexSettings))
    }
  }, [dispatch, flexSettings, sitePage.id])

  const saveAsDefault = useCallback(async () => {
    setIsActionInProgress(true)
    const newSitePage = {
      ...sitePage,
      defaultSettings: {
        ...sitePage.defaultSettings,
        ...JSON.parse(JSON.stringify(getAllWrappersInPage(sitePage.id, flexSettings, 'onlyArea'))),
        version: toServerFormat(new Date()),
      },
    }
    const newSitePages = sitePages.map((el) => {
      return el.id === newSitePage.id ? newSitePage : el
    })
    dispatch(setSitePages(newSitePages))
    setSitePage(newSitePage)
    await onSaveWidgetsAsDefault(newSitePage)
    await deleteLocalSettings()
    dispatch(setEditLayoutMode(false))
    setIsDefault(true)
    setIsActionInProgress(false)
  }, [
    sitePage,
    flexSettings,
    sitePages,
    dispatch,
    setSitePage,
    onSaveWidgetsAsDefault,
    deleteLocalSettings,
    setIsDefault,
  ])

  const resetToDefault = useCallback(async () => {
    setIsActionInProgress(true)
    await deleteLocalSettings()
    const settings = await getDefaultPageSettings(routeParams['page'])
    const parseSettings = settings.defaultSettings[area || 0]
    setSettings(structuredClone({
      ...parseSettings,
      items: parseSettings?.items || [],
      version: parseSettings?.version || toServerFormat(new Date()),
    }))

    setIsDefault(true)
    setIsActionInProgress(false)
  }, [deleteLocalSettings, getDefaultPageSettings, routeParams, area, setSettings, setIsDefault])

  const contextValue = useMemo<IFlexGridWrapperActionsContext>(() => {
    return {
      saveAsDefault,
      resetToDefault,
      exportSettings,
      isActionInProgress,
    }
  }, [exportSettings, isActionInProgress, resetToDefault, saveAsDefault])

  useEffect(() => {
    if (calledFlexWrapperAction) {
      if (calledFlexWrapperAction === 'save') {
        saveAsDefault().catch(console.error)
      } else if (calledFlexWrapperAction === 'restore') {
        resetToDefault().catch(console.error)
      } else if (calledFlexWrapperAction === 'export') {
        exportSettings().catch(console.error)
      }
      dispatch(setCalledFlexWrapperAction(undefined))
    }
  }, [calledFlexWrapperAction, dispatch, exportSettings, resetToDefault, saveAsDefault])

  return (
    <FlexGridWrapperActionsContext.Provider value={contextValue}>
      <LoaderOverlay show={isActionInProgress} />
      {children}
    </FlexGridWrapperActionsContext.Provider>
  )
}
